import { useParams, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import Navbar from "../Components/Navbar";
import GameCard from "../Components/GameCard";
import { games } from "../data/games";
import "../Styles/Gameplay.css";
import Footer2 from "../Components/Footer";

import FullscreenIcon from "../Assets/fullscreen.png";
import ExitFullscreenIcon from "../Assets/exit-fullscreen.png";

import TrendingGif from "../Assets/TrendingGif.png";
import TopPlayerGif from "../Assets/TopPlayerGif.png";
import DifficultyGif from "../Assets/DifficultyGif.png";
import RecommendedGif from "../Assets/RecommendedGif.png";

const Gameplay = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const game = games.find((g) => g.id === parseInt(id));

  // Guard: User must have access to open this gameplay page
  useEffect(() => {
    if (!game) return;

    const isFree = game.id % 3 === 0;
    if (isFree) return;

    const isLoggedIn =
      localStorage.getItem("isLoggedIn") === "true" ||
      JSON.parse(localStorage.getItem("userAuth") || "{}")?.loggedIn;

    const hasPaid = localStorage.getItem("hasPaidAccess") === "true";

    // Step 1 → If not logged in → open login modal
    if (!isLoggedIn) {
      localStorage.setItem(
        "postAuthRedirect",
        JSON.stringify({
          action: "checkout",
          gameId: game.id,
          path: window.location.pathname + window.location.search,
        })
      );
      window.dispatchEvent(new Event("openLogin"));
      return;
    }

    // Step 2 → If logged in but not paid → send to checkout
    if (!hasPaid) {
      navigate("/checkout", { state: { game } });
    }
  }, [game, navigate]);

  // OTHER GAME LISTS
  const genreGames = useMemo(
    () =>
      game
        ? games.filter((g) => g.genre === game.genre && g.id !== game.id)
        : [],
    [game]
  );

  const featuredGames = useMemo(() => games.slice(0, 10), []);

  if (!game) return <p className="no-games">Game not found</p>;

  return (
    <div
      className={`gameplay-container ${isFullScreen ? "fullscreen-mode" : ""}`}
    >
      {!isFullScreen && <Navbar />}

      <main className="main2-content">
        {!isFullScreen && (
          <h1 className="gameplay-title gradient-text">{game.title}</h1>
        )}

        {/* GAME FRAME */}
        <div className={`gameplay-frame ${isFullScreen ? "fullscreen" : ""}`}>
          <iframe
            src={game.embedLink}
            title={game.title}
            frameBorder="0"
            allow="autoplay; fullscreen; encrypted-media"
            allowFullScreen
            className="game-frame3"
          ></iframe>

          <div className="extbtn">
            {isFullScreen && (
              <img
                src={ExitFullscreenIcon}
                alt="Exit Fullscreen"
                className="exit-fullscreen-btn"
                onClick={() => setIsFullScreen(false)}
              />
            )}
          </div>
        </div>

        {/* FULLSCREEN BUTTON */}
        {!isFullScreen && (
          <div className="fullscreen-container">
            <img
              src={FullscreenIcon}
              alt="Fullscreen"
              className="fullscreen-btn"
              onClick={() => setIsFullScreen(true)}
            />
          </div>
        )}

        {/* GAME DESCRIPTION */}
        {!isFullScreen && (
          <section className="game-description glass-card">
            <div className="game-info-header">
              <h2 className="about-title">About {game.title}</h2>
            </div>

            {/* STATS */}
            <div className="game-stats-grid">
              <div className="stat-item">
                <strong>Platform:</strong> {game.platform}
              </div>
              <div className="stat-item">
                <strong>Players:</strong> {game.players}
              </div>
              <div className="stat-item">
                <strong>Online:</strong> {game.isOnline ? "Yes" : "No"}
              </div>
              <div className="stat-item">
                <strong>Rating:</strong> {game.rating}
              </div>
              <div className="stat-item">
                <strong>Release Year:</strong> {game.year}
              </div>
              <div className="stat-item">
                <strong>Genre:</strong> {game.genre}
              </div>
            </div>

            <div className="section-divider"></div>

            {/* OVERVIEW */}
            <div className="description-container">
              <h3 className="description-heading">Game Overview</h3>
              <p className="game-description-text">
                {game.title}, published by {game.publisher} in {game.year},
                offers an engaging experience in the {game.genre} genre.
                Available on {game.platform}, it supports {game.players} players
                and can be enjoyed {game.isOnline ? "online" : "offline"}. With
                a rating of {game.rating}, this game delivers thrilling
                gameplay, immersive design, and countless hours of entertainment.
              </p>
            </div>

            <div className="section-divider"></div>

            {/* MOCK HIGHLIGHTS */}
            <div className="highlights-section">
              <h3 className="highlights-heading">Game Highlights</h3>
              <div className="mock-section">
                <div className="mock-card">
                  <img src={TrendingGif} alt="" className="mock-icon-gif" />
                  <h4>Trending Game</h4>
                  <p>{game.title} is trending worldwide!</p>
                </div>
                <div className="mock-card">
                  <img src={TopPlayerGif} alt="" className="mock-icon-gif" />
                  <h4>Top Player Count</h4>
                  <p>{game.id * 101} active players daily</p>
                </div>
                <div className="mock-card">
                  <img src={DifficultyGif} alt="" className="mock-icon-gif" />
                  <h4>Difficulty</h4>
                  <p>{game.genre === "Action" ? "Hard" : "Medium"}</p>
                </div>
                <div className="mock-card">
                  <img src={RecommendedGif} alt="" className="mock-icon-gif" />
                  <h4>Recommended For</h4>
                  <p>{game.platform} enthusiasts</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* RELATED GAMES */}
        {!isFullScreen && genreGames.length > 0 && (
          <section className="related-section more-game1">
            <div className="section2-header">
              <h2>More {game.genre} Games</h2>
            </div>

            <div className="game2-grid">
              {genreGames.map((g) => (
                <GameCard key={g.id} game={g} />
              ))}
            </div>
          </section>
        )}

        {/* FEATURED GAMES */}
        {!isFullScreen && (
          <section className="related-section more-game1">
            <div className="section2-header">
              <h2>Featured Games</h2>
            </div>

            <div className="game2-grid">
              {featuredGames.map((g) => (
                <GameCard key={g.id} game={g} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer2 />
    </div>
  );
};

export default Gameplay;
